// One live subscription state for the whole app.
//
// Before this, three different things claimed to know what plan you were on
// and none of them agreed for long:
//
//   - useAuthPermissions read user_metadata.role off the JWT, which is fixed
//     at sign-in and does not move when you subscribe, upgrade or cancel
//   - useSubscriptionTier kept a 30-second module cache that nothing
//     invalidated when the subscription actually changed
//   - ProfileTopNav fetched /billing/subscription on its own, bypassing both
//
// So the pricing modal, the profile menu and the feature gates could each be
// showing a different plan at the same moment. This provider is the single
// source: it fetches once, shares that state with every consumer, and
// refreshes on the events that can change it.
//
// Refresh triggers, and why each is here:
//   sign-in / sign-out    the state belongs to an account
//   window focus          a subscription can be cancelled in the Stripe
//                         portal, in another tab, or on another device
//   returning from        Stripe redirects the browser back before its webhook
//   checkout              has necessarily landed, so that case polls
//   billing actions       cancel and resume change state from inside the app

import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  billingService, SubscriptionState, SubscriptionTier,
} from '../services/Billing/billingService';
import { useAuthContext } from './AuthProvider';
import { logger } from '../utils/logger';

// Tier ordering. Anything Premium unlocks, Premium Plus unlocks too — so a
// check for 'premium' is satisfied by a 'premium_plus' subscriber, which is
// the thing an `=== 'premium'` comparison gets wrong.
const TIER_RANK: Record<SubscriptionTier, number> = {
  premium: 1,
  premium_plus: 2,
};

const NO_SUBSCRIPTION: SubscriptionState = {
  hasSubscription: false,
  status: null,
  tier: null,
  cancelAtPeriodEnd: false,
  currentPeriodEnd: null,
  isPremium: false,
  canCancel: false,
  canResume: false,
  canResubscribe: false,
  pendingTier: null,
  pendingTierEffectiveAt: null,
};

// A focus refresh should not fire on every alt-tab.
const FOCUS_REFRESH_INTERVAL_MS = 30_000;

// Stripe's redirect regularly beats its own webhook, so a single read on
// return from checkout can legitimately still say "free". These are the
// delays between re-reads, in ms — roughly 11 seconds of patience in total,
// after which the focus refresh will pick it up anyway.
const BILLING_SETTLE_DELAYS_MS = [0, 1_000, 2_000, 3_000, 5_000];

export interface SubscriptionContextValue {
  /** The full billing state, for anything that needs canCancel/canResume. */
  subscription: SubscriptionState;
  /** The active tier, or null when there is no live subscription. */
  tier: SubscriptionTier | null;
  isPremium: boolean;
  isPremiumPlus: boolean;
  /** True when the tier is at least `required` — Plus satisfies 'premium'. */
  hasTier: (required: SubscriptionTier) => boolean;
  /** A cancellation is scheduled but the paid period is still running. */
  cancelAtPeriodEnd: boolean;
  loading: boolean;
  /** Re-read now. */
  refresh: () => Promise<void>;
  /**
   * Re-read repeatedly until the state changes or the attempts run out. For
   * use straight after a billing action, where the change is expected but may
   * not have reached Stripe's webhook yet.
   */
  refreshAfterBillingChange: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextValue | null>(null);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthContext();
  const [subscription, setSubscription] = useState<SubscriptionState>(NO_SUBSCRIPTION);
  const [loading, setLoading] = useState(true);

  // Kept in a ref as well so the polling loop can compare against the latest
  // value without re-creating itself on every state change.
  const latest = useRef<SubscriptionState>(NO_SUBSCRIPTION);
  const lastFetchedAt = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const read = useCallback(async (): Promise<SubscriptionState> => {
    if (!isAuthenticated) return NO_SUBSCRIPTION;
    try {
      const next = await billingService.getSubscription();
      lastFetchedAt.current = Date.now();
      return next;
    } catch (error) {
      // Treated as "no subscription": a billing outage should degrade to the
      // free experience, never hand out paid features by accident.
      logger.log('[SUBSCRIPTION] Could not read subscription state:', error);
      return NO_SUBSCRIPTION;
    }
  }, [isAuthenticated]);

  const apply = useCallback((next: SubscriptionState) => {
    latest.current = next;
    if (mounted.current) setSubscription(next);
  }, []);

  const refresh = useCallback(async () => {
    const next = await read();
    apply(next);
  }, [read, apply]);

  const refreshAfterBillingChange = useCallback(async () => {
    const before = latest.current;
    for (const delay of BILLING_SETTLE_DELAYS_MS) {
      if (delay) await new Promise((resolve) => setTimeout(resolve, delay));
      if (!mounted.current) return;

      const next = await read();
      apply(next);

      // Any of these moving means the webhook has landed; stop early rather
      // than sitting through the remaining delays.
      const changed = next.tier !== before.tier
        || next.isPremium !== before.isPremium
        || next.status !== before.status
        || next.cancelAtPeriodEnd !== before.cancelAtPeriodEnd;
      if (changed) return;
    }
    logger.log('[SUBSCRIPTION] No change seen after a billing action; the webhook may still be in flight.');
  }, [read, apply]);

  // Sign-in and sign-out. Signing out clears immediately rather than waiting
  // for a fetch to fail, so the next account never sees the last one's plan.
  useEffect(() => {
    if (!isAuthenticated) {
      apply(NO_SUBSCRIPTION);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    read()
      .then((next) => { if (!cancelled) apply(next); })
      .finally(() => { if (!cancelled && mounted.current) setLoading(false); });

    return () => { cancelled = true; };
  }, [isAuthenticated, read, apply]);

  // A subscription can change outside this tab — the Stripe billing portal,
  // another device, a card that failed. Re-reading on focus is what keeps the
  // app from insisting someone is Premium after they cancelled elsewhere.
  useEffect(() => {
    if (!isAuthenticated) return;

    const onFocus = () => {
      if (document.visibilityState === 'hidden') return;
      if (Date.now() - lastFetchedAt.current < FOCUS_REFRESH_INTERVAL_MS) return;
      refresh();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [isAuthenticated, refresh]);

  const value = useMemo<SubscriptionContextValue>(() => {
    // isPremium rather than status: a subscription cancelled but still inside
    // its paid period is not 'active', yet the user has paid through it.
    const active = !!subscription.isPremium;
    const tier = active ? (subscription.tier ?? null) : null;

    return {
      subscription,
      tier,
      isPremium: active,
      isPremiumPlus: !!tier && TIER_RANK[tier] >= TIER_RANK.premium_plus,
      hasTier: (required: SubscriptionTier) => !!tier && TIER_RANK[tier] >= TIER_RANK[required],
      cancelAtPeriodEnd: !!subscription.cancelAtPeriodEnd,
      loading,
      refresh,
      refreshAfterBillingChange,
    };
  }, [subscription, loading, refresh, refreshAfterBillingChange]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

/**
 * The live subscription state.
 *
 * Falls back to a free, non-loading state when no provider is above it, so a
 * component rendered outside the tree (a test, a storybook) degrades to the
 * free experience rather than crashing — and never to a paid one.
 */
export function useSubscriptionContext(): SubscriptionContextValue {
  const context = useContext(SubscriptionContext);
  if (context) return context;

  return {
    subscription: NO_SUBSCRIPTION,
    tier: null,
    isPremium: false,
    isPremiumPlus: false,
    hasTier: () => false,
    cancelAtPeriodEnd: false,
    loading: false,
    refresh: async () => {},
    refreshAfterBillingChange: async () => {},
  };
}
